import {
  managedRoleListSchema,
  managedRoleSchema,
  roleMemberSchema,
  roleMembersSchema,
  type CreateRoleRequest,
  type RoleMembers,
  type UpdateRoleRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/lib/api';
import { userLookupsQueryKey, usersQueryKey } from '@/platform/features/users/use-users';

const roleDeletedSchema = z.object({ ok: z.literal(true) });

/** `DELETE` sends `{ count, items }`. A full `roleMembersSchema` payload is accepted too. */
const roleInUseDetailsSchema = z.union([
  roleMembersSchema,
  z.object({
    count: z.number().int().nonnegative(),
    items: z.array(roleMemberSchema).max(50),
  }),
]);

export const rolesQueryKey = ['roles'] as const;

export function useRoles() {
  return useQuery({
    queryKey: rolesQueryKey,
    queryFn: () => apiFetch('/roles', managedRoleListSchema),
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoleRequest) =>
      apiFetch('/roles', managedRoleSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rolesQueryKey }),
        queryClient.invalidateQueries({ queryKey: userLookupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: usersQueryKey }),
      ]);
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRoleRequest }) =>
      apiFetch(`/roles/${id}`, managedRoleSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rolesQueryKey }),
        queryClient.invalidateQueries({ queryKey: userLookupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: usersQueryKey }),
      ]);
    },
  });
}

export function fetchRoleMembers(id: string): Promise<RoleMembers> {
  return apiFetch(`/roles/${id}/members`, roleMembersSchema);
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/roles/${id}`, roleDeletedSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: rolesQueryKey }),
        queryClient.invalidateQueries({ queryKey: userLookupsQueryKey }),
        queryClient.invalidateQueries({ queryKey: usersQueryKey }),
      ]);
    },
  });
}

export function roleMembersFromInUse(
  role: { id: string; code: string; name: string; isSystem: boolean },
  details: unknown,
): RoleMembers | null {
  const parsed = roleInUseDetailsSchema.safeParse(details);
  if (!parsed.success) {
    return null;
  }
  if ('code' in parsed.data) {
    return parsed.data;
  }
  return {
    id: role.id,
    code: role.code,
    name: role.name,
    isSystem: role.isSystem,
    count: parsed.data.count,
    items: parsed.data.items,
  };
}
